import React, { VFC } from 'react';
import { Switch, BrowserRouter, Route } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import sentryInitOption from '../../shared/utils/sentrySettings/initOption';
import GlobalStyle from '../styles/GlobalStyle';
import ErrorBoundary from '../../shared/components/ErrorBoundary';
import ErrorBoundaryComponent from '../components/Layouts/ErrorBoundary/Component';
import FetchAuth from '../components/Auth/FetchAuth';
import BaseLayoutRoute from '../components/LayoutRoutes/BaseLayoutRoute';
import AuthRequiredBaseLayoutRoute from '../components/LayoutRoutes/AuthRequiredBaseLayoutRoute';
import AppViewLayoutRoute from '../components/LayoutRoutes/AppViewLayoutRoute';
// routing info
import {
  APP_VIEW,
  ABOUT,
  BUSINESSES,
  BUSINESSES_DETAIL,
  PARTICIPANT_SCHEDULE,
  PROVIDER_CONFIRM,
  PROVIDER_CONFIRM_COMPLETE,
  USER_HISTORIES,
  USER_PAYMENT_HISTORY,
  USER_WITHHOLDINGS,
  USER_SCHEDULES,
  WITHDRAW,
  PLANS,
  CONTACT_SHOW,
  UNPROCESSABLE_ENTITY,
  INTERNAL_SERVER_ERROR,
  MAINTENANCE,
  OFFLINE,
} from '../constants/routing';
// pages
import { ApplicantsPage } from './Applicants/Loadable';
import PasswordResetRoute from './PasswordReset';
import { ProviderConfirmPage } from './ProviderConfirm/Loadable';
import { ProviderConfirmCompletePage } from './ProviderConfirmComplete/Loadable';
import { PlansPage } from './Plans/Loadable';
import { UserPaymentHistoryPage } from './UserPaymentHistory/Loadable';
import { UserWithdrawPage } from './Withdraw/Loadable';
import { AboutPage } from './About/Loadable';
import { BusinessesPage } from './Businesses/Loadable';
import { UserWithholdings } from './User/Withholdings/Loadable';
import MunicipalityCollaborationRoute from './MunicipalityCollaboration';
import { BusinessesDetail } from './BusinessesDetail/Loadable';
import { ContactShowPage } from './Contact/Show/Loadable';
import { ParticipantWorkingTimeDetailPage } from './ParticipantWorkingTime/Detail/Loadable';
import { ParticipantWorkingTimeWorkingPage } from './ParticipantWorkingTime/Working/Loadable';
import NotFoundPage from './Errors/NotFound';
import UnprocessableEntityPage from './Errors/UnprocessableEntity';
import InternalServerErrorPage from './Errors/InternalServerError';
import MaintenancePage from './Errors/Maintenance';
import OfflinePage from './Errors/Offline';

interface Props {}
const Router: VFC<Props> = () => {
  return (
    <BrowserRouter>
      <GlobalStyle />
      <ErrorBoundary Component={ErrorBoundaryComponent}>
        <Switch>
          <AppViewLayoutRoute path={APP_VIEW}>
            <PasswordResetRoute />
          </AppViewLayoutRoute>
          <Route>
            <FetchAuth>
              <Switch>
                {/* 認証と非認証の状態があるページ（トップ等）は、この下に追加していく */}
                <BaseLayoutRoute
                  path="/password_reset"
                  component={PasswordResetRoute}
                />
                <BaseLayoutRoute exact path={ABOUT} component={AboutPage} />
                <BaseLayoutRoute
                  exact
                  path={BUSINESSES}
                  component={BusinessesPage}
                />
                <BaseLayoutRoute
                  path="/mcpr"
                  component={MunicipalityCollaborationRoute}
                />
                <BaseLayoutRoute
                  exact
                  path={BUSINESSES_DETAIL}
                  component={BusinessesDetail}
                />
                <BaseLayoutRoute
                  exact
                  path={PROVIDER_CONFIRM}
                  component={ProviderConfirmPage}
                />
                <BaseLayoutRoute
                  exact
                  path={PROVIDER_CONFIRM_COMPLETE}
                  component={ProviderConfirmCompletePage}
                />
                <BaseLayoutRoute
                  exact
                  path={CONTACT_SHOW}
                  component={ContactShowPage}
                />
                <BaseLayoutRoute exact path={PLANS} component={PlansPage} />
                {/* 認証が必要なページはこの下に追加していく */}
                <AuthRequiredBaseLayoutRoute
                  exact
                  path={PARTICIPANT_SCHEDULE}
                  component={ParticipantWorkingTimeDetailPage}
                  displayFooter={false}
                />
                <AuthRequiredBaseLayoutRoute
                  exact
                  path={USER_HISTORIES}
                  component={ApplicantsPage}
                />
                <AuthRequiredBaseLayoutRoute
                  exact
                  path={USER_PAYMENT_HISTORY}
                  component={UserPaymentHistoryPage}
                />
                <AuthRequiredBaseLayoutRoute
                  exact
                  path={WITHDRAW}
                  component={UserWithdrawPage}
                />
                <AuthRequiredBaseLayoutRoute
                  exact
                  path={USER_WITHHOLDINGS}
                  component={UserWithholdings}
                />
                <AuthRequiredBaseLayoutRoute
                  exact
                  path={USER_SCHEDULES}
                  component={ParticipantWorkingTimeWorkingPage}
                  displayFooter={false}
                />
                {/* エラーページ */}
                <BaseLayoutRoute
                  path={UNPROCESSABLE_ENTITY}
                  component={UnprocessableEntityPage}
                />
                <BaseLayoutRoute
                  path={INTERNAL_SERVER_ERROR}
                  component={InternalServerErrorPage}
                />
                <BaseLayoutRoute
                  path={MAINTENANCE}
                  component={MaintenancePage}
                />
                <BaseLayoutRoute path={OFFLINE} component={OfflinePage} />
                {/* 404 は一番最後 */}
                <BaseLayoutRoute path="*" component={NotFoundPage} />
              </Switch>
            </FetchAuth>
          </Route>
        </Switch>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Router;

Sentry.init(sentryInitOption);
